import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type TaxBasis, type TaxSummary, formatMoney } from '@worksy360/shared';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Select, Skeleton, StatCard } from '../ui/index.js';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [0, 1, 2, 3].map((d) => CURRENT_YEAR - d);
const QUARTERS = [1, 2, 3, 4] as const;
const METHOD_LABEL: Record<string, string> = { card: 'Card', ach: 'Bank (ACH)', cash: 'Cash', check: 'Check', wallet: 'Digital wallet', other: 'Other' };

export function TaxCenter() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [mode, setMode] = useState<'year' | 'quarter'>('year');
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>((Math.floor(new Date().getMonth() / 3) + 1) as 1 | 2 | 3 | 4);
  const [basis, setBasis] = useState<TaxBasis>('cash');
  const period = mode === 'year' ? String(year) : `${year}-Q${quarter}`;

  const q = useQuery({ queryKey: ['tax-summary', period, basis], queryFn: () => api.get<TaxSummary>(`/reports/tax/summary?period=${period}&basis=${basis}`) });
  const s = q.data;
  const fmt = (c: number) => formatMoney(c, s?.currency ?? 'USD');

  return (
    <>
      <div className="page-head no-print">
        <div><div className="page-title">Tax Center</div><div className="page-sub">Year-end &amp; quarterly summaries for your accountant</div></div>
        <div className="row gap-2">
          <Button variant="ghost" onClick={() => s && downloadCsv(s)} disabled={!s}>⤓ CSV</Button>
          <Button onClick={() => window.print()} disabled={!s}>⎙ Print / Save PDF</Button>
        </div>
      </div>

      <Card className="no-print" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="card-pad row gap-3" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="stack gap-1"><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Period</span>
            <div className="row gap-1">
              <Button size="sm" variant={mode === 'year' ? 'primary' : 'ghost'} onClick={() => setMode('year')}>Full year</Button>
              <Button size="sm" variant={mode === 'quarter' ? 'primary' : 'ghost'} onClick={() => setMode('quarter')}>Quarter</Button>
            </div>
          </label>
          <label className="stack gap-1"><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Year</span>
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 120 }}>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
          </label>
          {mode === 'quarter' && (
            <label className="stack gap-1"><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Quarter</span>
              <Select value={quarter} onChange={(e) => setQuarter(Number(e.target.value) as 1 | 2 | 3 | 4)} style={{ width: 120 }}>
                {QUARTERS.map((n) => <option key={n} value={n}>{`Q${n}`}</option>)}
              </Select>
            </label>
          )}
          <label className="stack gap-1"><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Accounting basis</span>
            <div className="row gap-1">
              <Button size="sm" variant={basis === 'cash' ? 'primary' : 'ghost'} onClick={() => setBasis('cash')}>Cash</Button>
              <Button size="sm" variant={basis === 'accrual' ? 'primary' : 'ghost'} onClick={() => setBasis('accrual')}>Accrual</Button>
            </div>
          </label>
        </div>
      </Card>

      {q.isLoading ? <Card><div className="card-pad"><Skeleton h={220} /></div></Card> : !s ? null : (
        <div className="stack gap-4" id="tax-report">
          <div className="print-only" style={{ marginBottom: 'var(--sp-2)' }}>
            <h2 style={{ margin: 0 }}>Tax summary — {s.period.label}</h2>
            <div className="subtle" style={{ fontSize: 'var(--text-sm)' }}>{basisLabel(s.basis)} basis · generated {new Date(s.generatedAt).toLocaleDateString()} · figures in {s.currency}</div>
          </div>

          <div className="grid grid-4">
            {s.basis === 'cash' ? (
              <>
                <StatCard label="Net receipts" value={fmt(s.income.netReceiptsCents)} sub="Gross less refunds" tone="success" />
                <StatCard label="Gross receipts" value={fmt(s.income.grossReceiptsCents)} sub={`${s.income.paymentCount} payments`} />
                <StatCard label="Refunds" value={fmt(s.income.refundsCents)} />
              </>
            ) : (
              <>
                <StatCard label="Revenue invoiced" value={fmt(s.income.grossReceiptsCents)} sub={`${s.income.paymentCount} invoices issued`} tone="success" />
                <StatCard label="Taxable sales" value={fmt(s.salesTax.taxableSalesCents)} />
                <StatCard label="Gross invoiced" value={fmt(s.salesTax.grossInvoicedCents)} sub="incl. tax" />
              </>
            )}
            <StatCard label="Sales tax collected" value={fmt(s.salesTax.salesTaxCents)} sub={`${s.salesTax.invoicesPaidCount} invoices ${s.basis === 'cash' ? 'paid' : 'issued'}`} />
          </div>

          {s.basis === 'cash' && (
            <Card>
              <div className="card-head"><span className="card-title">Receipts by payment method</span></div>
              <div className="card-pad">
                <DataTable head={['Method', 'Payments', 'Gross', 'Net']}
                  rows={s.income.byMethod.map((m) => [METHOD_LABEL[m.method] ?? m.method, String(m.count), fmt(m.grossCents), fmt(m.netCents)])}
                  empty="No payments in this period" />
              </div>
            </Card>
          )}

          <Card>
            <div className="card-head"><span className="card-title">Sales tax collected</span><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>on invoices {s.basis === 'cash' ? 'marked paid' : 'issued'} this period</span></div>
            <div className="card-pad">
              <DataTable head={['Tax rate', 'Taxable sales', 'Tax collected', 'Invoices']}
                rows={s.salesTax.byRate.map((r) => [`${r.ratePercent}%`, fmt(r.taxableCents), fmt(r.taxCents), String(r.count)])}
                empty="No paid invoices in this period" />
            </div>
          </Card>

          <Card>
            <div className="card-head"><span className="card-title">1099-K</span>
              <Badge tone={s.form1099k.meetsFederalThreshold ? 'warning' : 'neutral'}>
                {s.form1099k.meetsFederalThreshold ? 'At/above federal threshold' : 'Below federal threshold'}
              </Badge>
            </div>
            <div className="card-pad stack gap-3">
              <div className="grid grid-3">
                <StatCard label="Card / wallet volume" value={fmt(s.form1099k.cardVolumeCents)} />
                <StatCard label="Card / wallet transactions" value={String(s.form1099k.cardCount)} />
                <StatCard label="Federal threshold" value={`${fmt(s.form1099k.thresholdAmountCents)} & ${s.form1099k.thresholdCount}`} sub="both must be exceeded" />
              </div>
              <p className="subtle" style={{ fontSize: 'var(--text-sm)' }}>{s.form1099k.note}</p>
              {s.form1099k.stripeDashboardUrl && (
                <div className="no-print"><a href={s.form1099k.stripeDashboardUrl} target="_blank" rel="noopener noreferrer"><Button size="sm">Open 1099-K in Stripe ↗</Button></a></div>
              )}
            </div>
          </Card>

          <details className="no-print">
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 'var(--text-sm)' }}>
              Transaction detail ({s.basis === 'cash' ? `${s.payments.length} payments · ` : ''}{s.paidInvoices.length} invoices)
            </summary>
            <div className="stack gap-3" style={{ marginTop: 'var(--sp-3)' }}>
              {s.basis === 'cash' && (
                <DataTable head={['Date', 'Payment', 'Method', 'Amount']}
                  rows={s.payments.map((l) => [l.date.slice(0, 10), l.ref, METHOD_LABEL[l.method ?? ''] ?? l.method ?? '', fmt(l.grossCents)])}
                  empty="No payments" />
              )}
              <DataTable head={[s.basis === 'cash' ? 'Paid' : 'Issued', 'Invoice', 'Subtotal', 'Tax', 'Total']}
                rows={s.paidInvoices.map((l) => [l.date.slice(0, 10), l.ref, fmt(l.subtotalCents ?? 0), fmt(l.taxCents ?? 0), fmt(l.grossCents)])}
                empty={`No invoices ${s.basis === 'cash' ? 'paid' : 'issued'} this period`} />
            </div>
          </details>

          <p className="subtle" style={{ fontSize: 'var(--text-xs)' }}>
            Generated from your Worksy360 invoices and payments for tax-preparation use. {s.basis === 'cash'
              ? 'Cash basis: income is recognized when paid (by payment date); sales tax reflects invoices marked paid in the period.'
              : 'Accrual basis: income is recognized when billed (by invoice date) for all issued invoices; sales tax reflects invoices issued in the period.'} Confirm with your accountant before filing.
          </p>
        </div>
      )}
    </>
  );
}

function basisLabel(basis: TaxBasis): string {
  return basis === 'cash' ? 'Cash' : 'Accrual';
}

function DataTable({ head, rows, empty }: { head: string[]; rows: string[][]; empty: string }) {
  if (rows.length === 0) return <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>{empty}</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr>{head.map((h, i) => <th key={h} style={i === 0 ? undefined : { textAlign: 'right' }}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => (
          <tr key={ri}>{r.map((c, ci) => <td key={ci} className={ci === 0 ? undefined : 'tabular'} style={ci === 0 ? undefined : { textAlign: 'right' }}>{c}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

/** Build and download a CSV an accountant can open in Excel — summary block + line detail. */
function downloadCsv(s: TaxSummary): void {
  const c = (n: number) => (n / 100).toFixed(2);
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const row = (...cells: (string | number)[]) => cells.map((x) => esc(String(x))).join(',');
  const lines: string[] = [
    row('Worksy360 Tax Summary', s.period.label),
    row('Basis', basisLabel(s.basis)),
    row('Generated', s.generatedAt),
    row('Currency', s.currency),
    '',
    row('Summary', 'Amount'),
    row('Gross receipts', c(s.income.grossReceiptsCents)),
    row('Refunds', c(s.income.refundsCents)),
    row('Net receipts', c(s.income.netReceiptsCents)),
    row('Taxable sales', c(s.salesTax.taxableSalesCents)),
    row('Sales tax collected', c(s.salesTax.salesTaxCents)),
    row('Card/wallet volume', c(s.form1099k.cardVolumeCents)),
    row('Card/wallet transactions', s.form1099k.cardCount),
    '',
    row('Receipts by method', 'Count', 'Gross', 'Net'),
    ...s.income.byMethod.map((m) => row(METHOD_LABEL[m.method] ?? m.method, m.count, c(m.grossCents), c(m.netCents))),
    '',
    row('Sales tax by rate', 'Taxable', 'Tax', 'Invoices'),
    ...s.salesTax.byRate.map((r) => row(`${r.ratePercent}%`, c(r.taxableCents), c(r.taxCents), r.count)),
    '',
    row('Payments detail'),
    row('Date', 'Payment', 'Method', 'Amount'),
    ...s.payments.map((l) => row(l.date.slice(0, 10), l.ref, METHOD_LABEL[l.method ?? ''] ?? l.method ?? '', c(l.grossCents))),
    '',
    row('Paid invoices detail'),
    row('Date', 'Invoice', 'Subtotal', 'Tax', 'Total'),
    ...s.paidInvoices.map((l) => row(l.date.slice(0, 10), l.ref, c(l.subtotalCents ?? 0), c(l.taxCents ?? 0), c(l.grossCents))),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `worksy360-tax-${s.period.token}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
